import {
  useCreateOnPremImport,
  useGetAddressMutation,
} from "apollo/hooks/mutations";
import { useEnvContext } from "context/EnvContext";
import {
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
} from "core/consts";
import { removeSpaces } from "core/model/utils/strings";
import { Careseeker, QueryProgress } from "core/types";
import { Spinner } from "ds/ui";
import RSButton from "ds_legacy/components/RSButton";
import TextInputField from "ds_legacy/components/TextInputField";
import { ERROR_COLOR } from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { margin, padding, sizing } from "ds_legacy/materials/metrics";
import { Caption, FONT_SIZE_16 } from "ds_legacy/materials/typography";
import { useHL7ImportStatusContext } from "dsl/atoms/HL7ImportStatusContext";
import { AlertTriangleIcon } from "lucide-react";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { FormElementRenderProps, FormWatcher } from "react-forms-state";
import { useTracking } from "react-tracking";
import { useConnecterWithLoggedAccount, useLocale } from "reduxentities/hooks";
import { useTranslations } from "translations";
import ReimportModal from "./ReimportModal";
import mutateKisPatient from "./mutateKisPatient";
import useSSLImport from "./useSSLImport";

function ClearOnChange({
  importStatus,
  setImportStatus,
  watchedValue,
}: {
  importStatus: QueryProgress;
  setImportStatus: Dispatch<SetStateAction<QueryProgress>>;
  watchedValue: string;
}) {
  useEffect(() => {
    if (importStatus !== QUERY_PROGRESS_NOT_STARTED) {
      setImportStatus(QUERY_PROGRESS_NOT_STARTED);
    }
  }, [watchedValue]);

  return null;
}

function WarningCaption({ text }: { text: string }) {
  return (
    <HorizontalLayout>
      <AlertTriangleIcon
        size={FONT_SIZE_16}
        style={{
          color: ERROR_COLOR,
          margin: margin(0, 0, 0, 2),
        }}
      />
      <Caption error maxWidth={sizing(38)} whiteSpace="normal">
        {text}
      </Caption>
    </HorizontalLayout>
  );
}

function CallImport({
  callImport,
  externalIdQueryVar,
  kis_import,
  onChangeKisHelper,
}: {
  callImport: AnyFunction;
  externalIdQueryVar: string | undefined;
  kis_import: boolean;
  onChangeKisHelper: (value: any) => void;
}) {
  useLayoutEffect(() => {
    if (externalIdQueryVar && !kis_import) {
      const timeout = setTimeout(() => {
        callImport(externalIdQueryVar, onChangeKisHelper, kis_import);
      }, 0);

      return () => clearTimeout(timeout);
    }
  }, []);

  return null;
}

export default function ExternalIdKisField({
  careseeker,
  externalIdQueryVar,
  initialPatientId,
  textLabelWidth,
}: {
  careseeker: Careseeker;
  externalIdQueryVar?: string;
  initialPatientId?: string;
  textLabelWidth: string;
}) {
  const translations = useTranslations();
  const locale = useLocale();
  const envContext = useEnvContext();
  const { trackEvent } = useTracking();
  const externalIdRef = useRef<string | undefined>(
    initialPatientId || externalIdQueryVar,
  );
  const loggedAccount = useConnecterWithLoggedAccount({ cacheOnly: true });
  const {
    hasOneWaySync,
    on_premise_authorization_token,
    on_premise_domain_ssl,
  } = useHL7ImportStatusContext();

  const [importStatus, setImportStatus] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [externalId, setExternalId] = useState(
    initialPatientId || externalIdQueryVar,
  );
  const [modalOpen, setModalOpen] = useState(false);

  const [getAddress] = useGetAddressMutation();
  const [createOnPremImportMutation] = useCreateOnPremImport({
    careseekerId: careseeker.id,
  });

  const callImport = useSSLImport({
    on_premise_authorization_token,
    on_premise_domain_ssl,
    setImportStatus,
  });

  useEffect(() => {
    externalIdRef.current = externalId;
  }, [externalId]);

  return (
    <HorizontalLayout
      gap={margin(1)}
      padding={padding(0)}
      style={{ alignItems: "baseline" }}
      width={textLabelWidth}
    >
      <VerticalLayout width={textLabelWidth}>
        <TextInputField
          elementName="external_id"
          endAdornment={
            importStatus === QUERY_PROGRESS_PENDING && (
              <Spinner
                aria-valuemax={100}
                aria-valuemin={0}
                aria-valuetext={translations.general.loading}
                role="progressbar"
                size="xs"
                testId="import-status-spinner"
              />
            )
          }
          label={translations.patient.externalId}
          marginOverride={margin(0)}
          sideMutation={setExternalId}
          width={textLabelWidth}
        />
        <FormWatcher watchPath="external_id">
          {({ watchedValue }) => (
            <ClearOnChange
              importStatus={importStatus}
              setImportStatus={setImportStatus}
              watchedValue={watchedValue}
            />
          )}
        </FormWatcher>
        {importStatus === QUERY_PROGRESS_FAILED && (
          <WarningCaption text={translations.patient.kis.importError} />
        )}
      </VerticalLayout>
      <FormElementRenderProps
        elementName="_kis_helper_element"
        sideMutation={(
          response: AnyObject,
          mutateElement: (value: any, name: string) => void,
        ) => {
          mutateKisPatient({
            careseeker,
            createOnPremImportMutation,
            envContext,
            externalId: externalIdRef.current,
            getAddress,
            locale,
            loggedAccount,
            mutateElement,
            response,
            trackEvent,
            translations,
          });
        }}
      >
        {({
          onChange: onChangeKisHelper,
        }: {
          onChange: (value: any) => void;
        }) => (
          <FormWatcher watchPath="kis_import">
            {({ watchedValue: kis_import }) => (
              <>
                {hasOneWaySync && kis_import !== null && (
                  <CallImport
                    callImport={callImport}
                    externalIdQueryVar={externalIdQueryVar}
                    kis_import={kis_import}
                    onChangeKisHelper={onChangeKisHelper}
                  />
                )}
                {on_premise_domain_ssl && (
                  <RSButton
                    key="create"
                    color="primary"
                    disabled={
                      importStatus === QUERY_PROGRESS_PENDING || !hasOneWaySync
                    }
                    id="reimport_patient"
                    loading="na"
                    style={{ margin: margin(2, 0, 0, 0), whiteSpace: "nowrap" }}
                    tooltip={
                      importStatus === QUERY_PROGRESS_PENDING
                        ? undefined
                        : hasOneWaySync
                        ? translations.patient.kis.tooltipConnected
                        : translations.patient.kis.tooltipNotConnectedReimport
                    }
                    type="button"
                    variant="text"
                    onClick={() => setModalOpen(true)}
                  >
                    {translations.patient.kis.reimportPatientData}
                  </RSButton>
                )}
                <ReimportModal
                  open={modalOpen}
                  translations={translations}
                  onCancel={() => setModalOpen(false)}
                  onImport={() => {
                    callImport(removeSpaces(externalId), onChangeKisHelper);
                    setModalOpen(false);
                  }}
                />
              </>
            )}
          </FormWatcher>
        )}
      </FormElementRenderProps>
    </HorizontalLayout>
  );
}
